import { DateTime } from "luxon";

import {
  AnalyticsChannelType,
  ChannelType,
  ConversionFunnelType,
  CountryCodesFilterType,
  isAnalyticsChannel,
  LandingPageFilterItemType,
  LandingPageFilterType,
  LandingPageType,
} from "@intelligems/ig-types/schemas/analytics/base";
import { CreateExperienceCustomMetricFe } from "@intelligems/ig-types/fe-only/custom-events";
import {
  ExperienceFe,
  GenericExperienceFe,
} from "@intelligems/ig-types/fe-only/experiences";
import {
  getFirstStartedAtTs,
  getLastEndedAtTs,
  getPausedAtTs,
} from "@intelligems/ig-analytics/experience-status";
import type { AnalyticFiltersConfigType } from "@src/api/types/dashboards";
import {
  channelTypeList,
  deviceTypeList,
  getConversionStageReachedList,
  visitorTypeList,
} from "@src/components/AudienceFilters/utils";
import type { IExperienceDashboardFilters } from "@src/store/types/dashboardFilters";
import { ISitewideDashboardFilters } from "@src/store/types/sitewideDashboardFilters";

const CUSTOMER_TYPE_FILTER_SUPPORTED_FROM_TS = DateTime.fromISO(
  "2026-02-01T00:00:00.000Z"
).toMillis();

export const DEFAULT_CUSTOMER_TYPE_FILTER_DESCRIPTION =
  "Filter orders by customer type";

// Shape of the deprecated single-path `landingPage` filter. Declared here so the
// migration helper and the empty-value const below agree on one definition.
type LandingPageSinglePathFilter = {
  landingPageUrlPath: string | null;
  landingPageUrlPathFilterType: LandingPageFilterType | null;
};

const isNullLandingPageOperator = (filterType: string) =>
  ["isNull", "isNotNull"].includes(filterType);

export const emptyLandingPageFilter = (): LandingPageFilterItemType => ({
  filterType: "contains" as LandingPageFilterType,
  values: [],
});

// isNull/isNotNull rows need no value; all others require a non-empty string
export const isActiveLandingPageFilter = (
  f: LandingPageFilterItemType
): boolean => {
  if (isNullLandingPageOperator(f.filterType)) {
    return true;
  }
  return (f?.values?.[0]?.trim().length ?? 0) > 0;
};

// Always show at least one empty row in the dialog so users don't see a blank section
export const padLandingPageFilters = (
  filters: LandingPageFilterItemType[]
): LandingPageFilterItemType[] =>
  filters.length > 0 ? filters : [emptyLandingPageFilter()];

// The single-path `landingPage` filter is deprecated in favour of the
// multi-value `landingPageFilters` array. Filter state can still arrive with it
// set (share links, bookmarked URLs), so lift it into the array the drawer edits
// instead of hiding a filter that is still being applied to the query. Only
// fills in when the array is empty — an explicit multi-value selection wins.
export const migrateLandingPageToFilters = (
  landingPage: LandingPageSinglePathFilter | null | undefined,
  landingPageFilters: LandingPageFilterItemType[] | undefined
): LandingPageFilterItemType[] => {
  const existing = landingPageFilters ?? [];
  if (existing.length > 0) {
    return existing;
  }

  const filterType = landingPage?.landingPageUrlPathFilterType;
  if (!filterType) {
    return existing;
  }

  if (isNullLandingPageOperator(filterType)) {
    return [{ filterType, values: [] }];
  }

  const path = landingPage?.landingPageUrlPath?.trim();
  if (!path) {
    return existing;
  }

  return [{ filterType, values: [path] }];
};

export const EMPTY_LANDING_PAGE_SINGLE_FILTER: LandingPageSinglePathFilter = {
  landingPageUrlPath: null,
  landingPageUrlPathFilterType: null,
};

const normalizeTimestampToMillis = (timestamp: number): number => {
  return timestamp >= 10_000_000_000
    ? timestamp
    : DateTime.fromSeconds(timestamp).toMillis();
};

export const getInitialStartFilterTime = (
  resource: ExperienceFe,
  dashboardFilters: IExperienceDashboardFilters
): number | null => {
  if (dashboardFilters.startTime) {
    return dashboardFilters.startTime as number | null;
  } else {
    return getFirstStartedAtTs(resource.experienceActions);
  }
};

export const getLastEndFilterTime = (
  resource: ExperienceFe,
  dashboardFilters: IExperienceDashboardFilters
): number | null => {
  if (dashboardFilters.endTime) {
    return dashboardFilters.endTime as number | null;
  } else {
    return resource.category === "personalization"
      ? getPausedAtTs(resource.experienceActions)?.actionTs || null
      : getLastEndedAtTs(resource.experienceActions);
  }
};

// Defined as the length of getAllChannelTypes rather than a second, independent
// walk of channelTypeList. The "All Sources" round-trip compares the two — [] is
// expanded to getAllChannelTypes(true), and a selection of getChannelTypeListCount(true)
// collapses back to [] — so they must agree by construction. Counting
// channelTypeList directly held only while every entry happened to be an analytics
// channel; adding one audience-only channel to that shared list would make []
// expand to a set that never collapses back, leaving "All Sources" stuck unchecked.
export const getChannelTypeListCount = (includeOther?: boolean) =>
  getAllChannelTypes(includeOther).length;

// Returns the picker's channel options as analytics channels. channelTypeList is
// shared with audience targeting and is typed off the audience vocabulary, so it
// is filtered through isAnalyticsChannel here: an audience-only value would be
// rejected by the analytics filter schemas rather than silently forwarded.
export const getAllChannelTypes = (
  includeOther?: boolean
): AnalyticsChannelType[] => {
  const channelListCopy = [...channelTypeList];
  if (includeOther) {
    channelListCopy.push({ key: "Other", label: "Other" });
  }

  return channelListCopy
    .map((channelType): string | null => channelType.key)
    .filter(
      (key): key is AnalyticsChannelType =>
        key !== null && isAnalyticsChannel(key)
    );
};

export const getCustomerTypeFilterDescription = (
  experience?: GenericExperienceFe
): string => {
  const experienceStartTs = experience?.startedAtTs;
  const normalizedExperienceStartTs =
    experienceStartTs == null
      ? null
      : normalizeTimestampToMillis(experienceStartTs);

  if (
    normalizedExperienceStartTs == null ||
    normalizedExperienceStartTs >= CUSTOMER_TYPE_FILTER_SUPPORTED_FROM_TS
  ) {
    return DEFAULT_CUSTOMER_TYPE_FILTER_DESCRIPTION;
  }

  return `${DEFAULT_CUSTOMER_TYPE_FILTER_DESCRIPTION}. Filter available for orders from February 1st, 2026 onward.`;
};

export const shouldShowFilter = (
  filter: AnalyticFiltersConfigType,
  pageName?: string
) => {
  if (
    (filter.config === true || filter.config === undefined) &&
    filter.showOnPage === "all"
  ) {
    return true;
  } else if (filter.config === true || filter.config === undefined) {
    return pageName === filter.showOnPage || pageName !== filter.dontShowOnPage;
  } else {
    return false;
  }
};

export const getSelectedCount = (filter: AnalyticFiltersConfigType): number => {
  switch (filter.settings[0].key) {
    case "deviceType":
      return filter.settings[0].component.props.deviceType !== "any" ? 1 : 0;
    case "customerType":
      return filter.settings[0].component.props.customerType !== null ? 1 : 0;
    case "userBehavior":
      return filter.settings[0].component.props.conversionStageReached !==
        "any" ||
        filter.settings[0].component.props.userBehavior.viewedCollectionPage ||
        filter.settings[0].component.props.userBehavior.viewedProductPage
        ? 1
        : 0;
    case "visitorType":
      return filter.settings[0].component.props.visitorType !== "any" ? 1 : 0;
    case "countryCodes":
      return filter.settings[0].component.props.selectedValues?.length || 0;
    case "channelType":
      const totalChannels = getChannelTypeListCount(true);
      const selectedChannels =
        filter.settings[0].component.props.channels?.length || 0;
      return selectedChannels === totalChannels ? 0 : selectedChannels;
    case "urlParam": {
      const urlParam = filter.settings[0].component.props.urlParam;
      return Object.values(urlParam).filter((value) => value !== null).length >
        0
        ? 1
        : 0;
    }
    case "landingPage":
      return filter.settings[0].component.props.filter ? 1 : 0;
    case "landingPageFilters":
      return (
        (
          (filter.settings[0].component.props
            .filters as LandingPageFilterItemType[]) ?? []
        ).filter(isActiveLandingPageFilter).length || 0
      );
    case "customEvents":
      return (
        filter.settings[0].component.props.selectedCustomEventIds?.length || 0
      );

    case "shippingMethods":
      return filter.settings[0].component.props.selectedValues?.length || 0;

    case "minMaxNetRevenue":
      return filter.settings[0].component.props.minRevenue !== null ||
        filter.settings[0].component.props.maxRevenue !== null ||
        filter.settings[0].component.props.excludeFreeOrders === false
        ? 1
        : 0;

    case "minMaxNetShippingRevenue":
      return filter.settings[0].component.props.minRevenue !== null ||
        filter.settings[0].component.props.maxRevenue !== null
        ? 1
        : 0;
    case "analyticsViewType":
      return filter.settings[0].component.props.analyticsViewType ===
        "sitewide_orders" ||
        filter.settings[0].component.props.analyticsViewType === "none"
        ? 0
        : filter.settings[0].component.props.analyticsViewType ===
            "specific_products"
          ? filter.settings[0].component.props.selectedAnalyticsProducts.length
          : 1;
    case "sitewideUserBehavior": {
      const cf = filter.settings[0].component.props.conversionFunnel as
        | ConversionFunnelType
        | undefined;
      const hasStage = (
        [
          "addedToCart",
          "startedCheckout",
          "converted",
          "bounced",
          "abandonedCart",
          "abandonedCheckout",
        ] as const
      ).some((k) => cf?.[k] === true);
      return hasStage || cf?.viewedCollectionPage || cf?.viewedProductPage
        ? 1
        : 0;
    }
    case "landingPageType":
    case "secondPageType":
      return filter.settings[0].component.props.value ? 1 : 0;
    default:
      return 0;
  }
};

export const filterMatchesSearch = (
  filter: AnalyticFiltersConfigType,
  search: string,
  experienceCustomMetrics: CreateExperienceCustomMetricFe[]
) => {
  if (!search) {
    return true;
  }
  const searchLower = search.toLowerCase();

  // Check the filter label
  if (filter.label.toLowerCase().includes(searchLower)) {
    return true;
  }

  // Check each setting value
  return filter.settings.some((setting) => {
    switch (setting.key) {
      case "deviceType":
        return deviceTypeList
          .map((device) => device.label.toLowerCase())
          .some((deviceLabel) => deviceLabel.includes(searchLower));
      case "customerType":
        return ["all customers", "new customers", "returning customers"].some(
          (label) => label.includes(searchLower)
        );
      case "visitorType":
        return visitorTypeList
          .map((device) => device.label.toLowerCase())
          .some((deviceLabel) => deviceLabel.includes(searchLower));
      case "countryCodes":
        return setting.component.props.selectedValues?.some((value: string) =>
          value.toLowerCase().includes(searchLower)
        );
      case "channelType":
        return channelTypeList
          .map((device) => device.label.toLowerCase())
          .some((deviceLabel) => deviceLabel.includes(searchLower));
      case "reachedCheckoutStage":
        return getConversionStageReachedList({
          hasCheckoutBlocks: true,
          hasOnsiteUpsell: true,
          hasTestPostPurchase: true,
        })
          .map((stage) => stage.label.toLowerCase())
          .some((stage) => stage.includes(searchLower));
      case "userBehavior": {
        const userBehavior = setting.component.props.userBehavior;
        if (!userBehavior) {
          return false;
        }
        return Object.entries(userBehavior).some(([key, value]) => {
          return (
            key.toLowerCase().includes(searchLower) ||
            (value !== null &&
              // eslint-disable-next-line @typescript-eslint/no-base-to-string
              value?.toString().toLowerCase().includes(searchLower))
          );
        });
      }
      case "urlParam": {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access
        const urlParam = setting.component.props.urlParam;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        return Object.values(urlParam).some(
          (value) =>
            value !== null &&
            // eslint-disable-next-line @typescript-eslint/no-base-to-string
            value?.toString().toLowerCase().includes(searchLower)
        );
      }
      case "customEvents":
        return experienceCustomMetrics
          .map((event) => event.customEvent.name.toLowerCase())
          .some((eventName) => eventName.includes(searchLower));
      case "isZScoreEnabled":
        return (
          setting.component.props.maxZScore
            ?.toString()
            .toLowerCase()
            .includes(searchLower) || false
        );
      default:
        return false;
    }
  });
};

export const useDialogFilterValidation = (
  dialogFilters: IExperienceDashboardFilters
) => {
  const validationErrors: { isDisabled: boolean; reason: string }[] = [];

  if (!Object.values(dialogFilters.urlParam).every((param) => param == null)) {
    if (dialogFilters.urlParam.queryParam === null) {
      validationErrors.push({
        isDisabled: true,
        reason: "URL Parameter is required",
      });
    }
    if (
      (dialogFilters.urlParam.queryParamValue === null ||
        dialogFilters.urlParam.queryParamValue === "") &&
      !["isNull", "isNotNull"].includes(
        dialogFilters.urlParam.queryParamFilterType || ""
      )
    ) {
      validationErrors.push({
        isDisabled: true,
        reason: "URL Parameter value is required",
      });
    }
    if (dialogFilters.urlParam.queryParamFilterType === null) {
      validationErrors.push({
        isDisabled: true,
        reason: "URL Parameter filter type is required",
      });
    }
  }

  if (
    dialogFilters.landingPage &&
    !Object.values(dialogFilters.landingPage).every((param) => param == null)
  ) {
    if (
      (dialogFilters.landingPage.landingPageUrlPath == null ||
        dialogFilters.landingPage.landingPageUrlPath === "") &&
      !["isNull", "isNotNull"].includes(
        dialogFilters.landingPage.landingPageUrlPathFilterType || ""
      )
    ) {
      validationErrors.push({
        isDisabled: true,
        reason: "Landing Page URL Path is required",
      });
    }
    if (dialogFilters.landingPage.landingPageUrlPathFilterType == null) {
      validationErrors.push({
        isDisabled: true,
        reason: "Landing Page URL Path filter type is required",
      });
    }
  }

  return validationErrors;
};

export const useSitewideDrawerFilterValidation = (
  dialogFilters: ISitewideDashboardFilters
) => {
  const validationErrors: { isDisabled: boolean; reason: string }[] = [];

  if (
    dialogFilters.landingPage &&
    !Object.values(dialogFilters.landingPage).every((param) => param == null)
  ) {
    if (
      dialogFilters.landingPage.landingPageUrlPath == null &&
      !["isNull", "isNotNull"].includes(
        dialogFilters.landingPage.landingPageUrlPathFilterType || ""
      )
    ) {
      validationErrors.push({
        isDisabled: true,
        reason: "Landing Page URL Path is required",
      });
    }
    if (dialogFilters.landingPage.landingPageUrlPathFilterType == null) {
      validationErrors.push({
        isDisabled: true,
        reason: "Landing Page URL Path filter type is required",
      });
    }
  }

  return validationErrors;
};

// Display only relabel. Value is sent to analytics and should not change. See ENG-6341.
export const LANDING_PAGE_TYPE_LABELS: Partial<
  Record<LandingPageType, string>
> = {
  PDP: "Product",
};

export const getLandingPageTypeLabel = (pageType: LandingPageType): string =>
  LANDING_PAGE_TYPE_LABELS[pageType] ?? pageType;

export const countryCodesFilterTypeList: {
  label: string;
  key: CountryCodesFilterType;
}[] = [
  { label: "is one of", key: "include" },
  { label: "is not one of", key: "exclude" },
];
